import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import {
  OwnerEntity, getCurrentUser, getAccountOwner, getAccountOwnerForUser, resolveEntities,
} from '../utils/ownerEntities';
import { entityBaseCurrency } from '../utils/baseCurrency';
import { getProfitCenters } from '../utils/profitCenters';
import { manualOpeningTotal, findControlAccount } from '../utils/partyLedger';
import './GLReportsTab.css';

// =============================================
// Trial Balance & General Ledger (Reports → read-only)
// =============================================
// Reads the double-entry GL (acctg_gl_journal + _line) and the Chart of Accounts to
// present, per entity: a Trial Balance (each account's net on its normal side, with
// balanced grand totals) and a General Ledger (each account's posted lines with a
// running balance). Computed from POSTED (non-void) journals + each account's
// opening_balance — independent of the cached acctg_coa.current_balance. Bookkeeping
// only (Your / Client's Accounts), never DMS.
// =============================================

interface GLReportsTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

interface CoaRow {
  coa_id: number; account_owner_id: number; owner_type: string; owner_id: number;
  account_number: string | null; account_name: string; normal_balance?: string;
  opening_balance?: number | string | null; is_header: number; is_active?: number; is_deleted?: number;
  [k: string]: any;
}
interface JournalRow { journal_id: number; account_owner_id: number; owner_type: string; owner_id: number; journal_date: string; journal_number: string | null; memo: string | null; source: string; status: string; is_deleted?: number; [k: string]: any; }
interface JournalLineRow { line_id: number; journal_id: number; coa_id: number; debit: number; credit: number; line_memo: string | null; is_deleted?: number; [k: string]: any; }

const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const isCredit = (normal?: string) => /credit/i.test(normal || '');
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GLReportsTab: React.FC<GLReportsTabProps> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaRow[]>([]);
  const [journals, setJournals] = useState<JournalRow[]>([]);
  const [journalLines, setJournalLines] = useState<JournalLineRow[]>([]);
  const [locationsData, setLocationsData] = useState<Record<string, any>[]>([]);
  const [unitsData, setUnitsData] = useState<Record<string, any>[]>([]);
  const [vendorRows, setVendorRows] = useState<Record<string, any>[]>([]);
  const [customerRows, setCustomerRows] = useState<Record<string, any>[]>([]);

  const [ownerKey, setOwnerKey] = useState('');
  const [view, setView] = useState<'trial' | 'ledger'>('trial');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeOpening, setIncludeOpening] = useState(true);
  const [ledgerAcct, setLedgerAcct] = useState('');   // '' = all accounts (ledger view)
  const [profitCenter, setProfitCenter] = useState(''); // '' = all profit centers
  const [openAccts, setOpenAccts] = useState<Set<number>>(new Set());

  // ---- Load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, b, coa, j, jl, loc, un] = await Promise.all([
          loadTable('users'), loadTable('user_businesses'), loadTable('acctg_coa'),
          loadTable('acctg_gl_journal'), loadTable('acctg_gl_journal_line'),
          loadTable('user_business_location'), loadTable('user_business_unit'),
        ]);
        if (cancelled) return;
        setUsersData(u); setBusinessesData(b); setCoaAccounts(coa as CoaRow[]);
        setJournals(j as JournalRow[]); setJournalLines(jl as JournalLineRow[]);
        setLocationsData(loc); setUnitsData(un); setDataLoaded(true);
      } catch {
        if (cancelled) return;
        setUsersData(getTableData('users')); setBusinessesData(getTableData('user_businesses'));
        setCoaAccounts(getTableData('acctg_coa') as CoaRow[]);
        setJournals(getTableData('acctg_gl_journal') as JournalRow[]);
        setJournalLines(getTableData('acctg_gl_journal_line') as JournalLineRow[]);
        setLocationsData(getTableData('user_business_location'));
        setUnitsData(getTableData('user_business_unit')); setDataLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Vendors / customers, for the opening balances typed in Masters (best-effort).
  useEffect(() => {
    let cancelled = false;
    Promise.all(['acctg_vendor', 'acctg_customer'].map(t => loadTable(t).catch(() => getTableData(t)))).then(([v, c]) => {
      if (cancelled) return;
      setVendorRows(v as Record<string, any>[]); setCustomerRows(c as Record<string, any>[]);
    });
    return () => { cancelled = true; };
  }, []);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwnerId = useMemo(() => {
    if (!currentUser) return 0;
    const ao = getAccountOwnerForUser(currentUser.user_id, usersData); // logged-in user's own account (multi-tenant safe)
    return ao ? Number(ao.user_id) : 0;
  }, [currentUser, usersData]);

  const ownerEntities = useMemo<OwnerEntity[]>(
    () => dataLoaded ? resolveEntities(viewMode, accountOwnerId, selectedClientId, usersData, businessesData) : [],
    [dataLoaded, viewMode, accountOwnerId, selectedClientId, usersData, businessesData]);

  useEffect(() => {
    if (!ownerKey && ownerEntities.length) setOwnerKey(`${ownerEntities[0].owner_type}:${ownerEntities[0].owner_id}`);
  }, [ownerEntities, ownerKey]);

  const selectedEntity = useMemo(() => ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === ownerKey) || null, [ownerEntities, ownerKey]);
  // The books' base currency (Business setup / the entity's country) — named on the report.
  const reportCurrency = useMemo(
    () => (selectedEntity ? entityBaseCurrency(selectedEntity.owner_type, selectedEntity.owner_id, usersData, businessesData) : ''),
    [selectedEntity, usersData, businessesData]);

  // Profit centers for a business entity (locations/units flagged tracks_own_profitability).
  const profitCenters = useMemo(
    () => getProfitCenters(selectedEntity && selectedEntity.owner_type === 'business' ? selectedEntity.owner_id : null, locationsData, unitsData),
    [selectedEntity, locationsData, unitsData]);
  // Decode the active profit-center filter into ids (a location filter matches its units too).
  const pcFilter = useMemo(() => profitCenters.decode(profitCenter), [profitCenters, profitCenter]);

  // Detail accounts for the entity (transactions post here, not headers).
  const entityAccounts = useMemo(() => coaAccounts.filter(a =>
    a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
    // eslint-disable-next-line eqeqeq
    a.account_owner_id == accountOwnerId && selectedEntity &&
    a.owner_type === selectedEntity.owner_type &&
    // eslint-disable-next-line eqeqeq
    a.owner_id == selectedEntity.owner_id
  ), [coaAccounts, accountOwnerId, selectedEntity]);

  // Posted, non-void journals for the entity, within the optional date range.
  const entityJournals = useMemo(() => {
    if (!selectedEntity) return [] as JournalRow[];
    return journals.filter(j => {
      if (j.is_deleted === 1 || j.status !== 'posted') return false;
      if (j.owner_type !== selectedEntity.owner_type) return false;
      // eslint-disable-next-line eqeqeq
      if (j.owner_id != selectedEntity.owner_id || j.account_owner_id != accountOwnerId) return false;
      const d = (j.journal_date || '').slice(0, 10);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
  }, [journals, selectedEntity, accountOwnerId, fromDate, toDate]);

  // Lines of those journals, with their journal's date/number for the ledger view.
  // When a profit center is selected, keep only lines tagged to it (a location
  // filter includes that location's units via location_id; a unit filter matches unit_id).
  const activeLines = useMemo(() => {
    const jById = new Map(entityJournals.map(j => [Number(j.journal_id), j]));
    return journalLines
      .filter(l => {
        if (l.is_deleted === 1 || !jById.has(Number(l.journal_id))) return false;
        if (pcFilter.unit_id != null) return Number(l.unit_id) === Number(pcFilter.unit_id);
        // eslint-disable-next-line eqeqeq
        if (pcFilter.location_id != null) return l.location_id != null && l.location_id == pcFilter.location_id;
        return true;
      })
      .map(l => ({ ...l, _journal: jById.get(Number(l.journal_id))! }));
  }, [journalLines, entityJournals, pcFilter]);

  // Per-account aggregation: opening + posted activity.
  const accountRows = useMemo(() => {
    return entityAccounts.map(a => {
      const lines = activeLines.filter(l => Number(l.coa_id) === Number(a.coa_id));
      const debit = round2(lines.reduce((s, l) => s + num(l.debit), 0));
      const credit = round2(lines.reduce((s, l) => s + num(l.credit), 0));
      const opening = round2(num(a.opening_balance));
      // Net in DEBIT terms (positive = debit-side balance).
      const openingDebitTerms = includeOpening ? (isCredit(a.normal_balance) ? -opening : opening) : 0;
      const net = round2(openingDebitTerms + (debit - credit));
      return { acct: a, debit, credit, opening, net, lineCount: lines.length };
    });
  }, [entityAccounts, activeLines, includeOpening]);

  // Opening balances typed on vendors / customers in Masters that no opening-balances
  // journal entry carries: summed into Accounts payable / Accounts receivable, so the
  // Trial Balance shows what is owed. They have no other side until an opening entry is
  // posted for them — the report says so when it is out of balance. Not per profit center.
  const partyOpening = useMemo(() => {
    const none = { vendor: { total: 0, count: 0 }, customer: { total: 0, count: 0 } };
    if (!selectedEntity || !includeOpening || profitCenter) return none;
    const books = { account_owner_id: accountOwnerId, owner_type: selectedEntity.owner_type, owner_id: selectedEntity.owner_id };
    const args = { journals: journals as any[], lines: journalLines as any[], books, asOf: toDate || undefined };
    return {
      vendor: manualOpeningTotal({ ...args, parties: vendorRows, pk: 'vendor_id', kind: 'vendor' }),
      customer: manualOpeningTotal({ ...args, parties: customerRows, pk: 'customer_id', kind: 'customer' }),
    };
  }, [selectedEntity, includeOpening, profitCenter, accountOwnerId, journals, journalLines, vendorRows, customerRows, toDate]);

  // Trial Balance rows = accounts with any balance or activity. `partyNote` marks the
  // row that carries the vendors' / customers' typed opening balances; when the books
  // have no payables / receivables account, a row is shown for them on its own.
  const trialRows = useMemo(() => {
    type TrialRow = { key: string; label: string; net: number; partyNote: string };
    const rows: (TrialRow & { number: string })[] = accountRows.map(r => ({
      key: String(r.acct.coa_id), number: r.acct.account_number || '',
      label: `${r.acct.account_number ? `${r.acct.account_number} · ` : ''}${r.acct.account_name}`,
      net: r.net, partyNote: '', _keep: r.net !== 0 || r.lineCount > 0,
    } as any));
    const add = (kind: 'vendor' | 'customer', word: string, fallback: string) => {
      const { total, count } = partyOpening[kind];
      if (!total) return;
      const debitTerms = kind === 'customer' ? total : -total;   // payables sit on the credit side
      const note = `includes ${money(Math.abs(total))} of opening balances from ${count} ${word}${count === 1 ? '' : 's'} (Masters)`;
      const acct = findControlAccount(entityAccounts, kind);
      const row = acct ? rows.find(r => r.key === String(acct.coa_id)) : null;
      if (row) { row.net = round2(row.net + debitTerms); row.partyNote = note; (row as any)._keep = true; }
      else rows.push({ key: `party-${kind}`, number: kind === 'customer' ? '1200' : '2000', label: fallback, net: round2(debitTerms), partyNote: note.replace('includes ', ''), _keep: true } as any);
    };
    add('vendor', 'vendor', 'Accounts payable — vendor opening balances');
    add('customer', 'customer', 'Accounts receivable — customer opening balances');
    return rows.filter(r => (r as any)._keep).sort((a, b) => a.number.localeCompare(b.number));
  }, [accountRows, partyOpening, entityAccounts]);
  const hasPartyOpening = partyOpening.vendor.total !== 0 || partyOpening.customer.total !== 0;

  const trialTotals = useMemo(() => {
    let debit = 0, credit = 0;
    trialRows.forEach(r => { if (r.net >= 0) debit += r.net; else credit += -r.net; });
    return { debit: round2(debit), credit: round2(credit), diff: round2(debit - credit) };
  }, [trialRows]);

  // Ledger sections = accounts with activity (or a non-zero opening), running balance.
  const ledgerSections = useMemo(() => {
    const wanted = ledgerAcct ? accountRows.filter(r => String(r.acct.coa_id) === ledgerAcct)
      : accountRows.filter(r => r.lineCount > 0 || r.opening !== 0);
    return wanted
      .slice().sort((a, b) => (a.acct.account_number || '').localeCompare(b.acct.account_number || ''))
      .map(r => {
        const lines = activeLines.filter(l => Number(l.coa_id) === Number(r.acct.coa_id))
          .slice().sort((x, y) => ((x._journal.journal_date || '').localeCompare(y._journal.journal_date || '')) || Number(x._journal.journal_id) - Number(y._journal.journal_id));
        const credit = isCredit(r.acct.normal_balance);
        let running = includeOpening ? r.opening : 0;   // running balance in NORMAL terms
        const rows = lines.map(l => {
          const delta = credit ? (num(l.credit) - num(l.debit)) : (num(l.debit) - num(l.credit));
          running = round2(running + delta);
          return { line: l, debit: num(l.debit), credit: num(l.credit), balance: running };
        });
        return { acct: r.acct, opening: r.opening, rows, ending: round2(running) };
      });
  }, [accountRows, activeLines, ledgerAcct, includeOpening]);

  const toggleAcct = useCallback((coaId: number) => {
    setOpenAccts(prev => { const n = new Set(prev); if (n.has(coaId)) n.delete(coaId); else n.add(coaId); return n; });
  }, []);

  if (!dataLoaded) return <div className="glr-tab"><div className="glr-empty">Loading…</div></div>;

  const noEntity = ownerEntities.length === 0;

  return (
    <div className="glr-tab">
      <div className="glr-head">
        <h3 className="glr-title">Trial Balance &amp; General Ledger</h3>
        <p className="glr-sub">Computed from posted journal entries{includeOpening ? ' plus opening balances' : ''}. Voided entries are excluded.{reportCurrency ? ` Amounts in ${reportCurrency}.` : ''}</p>
      </div>

      {noEntity ? (
        <div className="glr-empty">
          {viewMode === 'clients' && selectedClientId == null ? 'Pick a client first.' : 'No accounts available — set up your Chart of Accounts first.'}
        </div>
      ) : (
        <>
          <div className="glr-controls">
            <div className="glr-field">
              <label className="glr-label">Entity</label>
              <SearchableDropdown
                className="glr-owner-select"
                options={ownerEntities.map(e => ({ value: `${e.owner_type}:${e.owner_id}`, label: `${e.display_name} (${e.badge})` }))}
                value={ownerKey}
                onChange={(v: string) => { setOwnerKey(v); setLedgerAcct(''); setOpenAccts(new Set()); setProfitCenter(''); }}
                placeholder="Choose an entity"
              />
            </div>
            {profitCenters.hasAny && (
              <div className="glr-field">
                <label className="glr-label">Profit center</label>
                <SearchableDropdown
                  className="glr-owner-select"
                  options={[{ value: '', label: 'All profit centers' }, ...profitCenters.options]}
                  value={profitCenter}
                  onChange={(v: string) => setProfitCenter(v)}
                  placeholder="All profit centers"
                />
              </div>
            )}
            <div className="glr-field">
              <label className="glr-label">From</label>
              <input type="date" className="glr-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="glr-field">
              <label className="glr-label">To</label>
              <input type="date" className="glr-input" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
            <label className="glr-check">
              <input type="checkbox" checked={includeOpening} onChange={e => setIncludeOpening(e.target.checked)} />
              Include opening balances
            </label>
          </div>

          <div className="glr-viewtabs">
            <button className={`glr-viewtab ${view === 'trial' ? 'active' : ''}`} onClick={() => setView('trial')}>Trial Balance</button>
            <button className={`glr-viewtab ${view === 'ledger' ? 'active' : ''}`} onClick={() => setView('ledger')}>General Ledger</button>
          </div>

          {view === 'trial' ? (
            <div className="glr-tb">
              {trialRows.length === 0 ? (
                <div className="glr-empty glr-empty-sm">No posted activity or balances for this entity{(fromDate || toDate) ? ' in this date range' : ''}.</div>
              ) : (
                <table className="glr-table">
                  <thead>
                    <tr><th>Account</th><th className="glr-num">Debit</th><th className="glr-num">Credit</th></tr>
                  </thead>
                  <tbody>
                    {trialRows.map(r => (
                      <tr key={r.key}>
                        <td>{r.label}{r.partyNote && <span className="glr-party-note"> — {r.partyNote}</span>}</td>
                        <td className="glr-num">{r.net > 0 ? money(r.net) : ''}</td>
                        <td className="glr-num">{r.net < 0 ? money(-r.net) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="glr-total-row">
                      <td>Total</td>
                      <td className="glr-num">{money(trialTotals.debit)}</td>
                      <td className="glr-num">{money(trialTotals.credit)}</td>
                    </tr>
                    <tr className={`glr-balance-row ${trialTotals.diff === 0 ? 'ok' : 'off'}`}>
                      <td colSpan={3}>
                        {trialTotals.diff === 0
                          ? 'In balance — debits equal credits.'
                          : `Out of balance by ${money(Math.abs(trialTotals.diff))}${hasPartyOpening
                            ? ' — vendor / customer opening balances typed in Masters have no other side yet; post an opening-balances journal entry naming the party to bring them into the books.'
                            : includeOpening ? ' — opening balances may not net to zero.' : '.'}`}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          ) : (
            <div className="glr-ledger">
              <div className="glr-field glr-ledger-filter">
                <label className="glr-label">Account</label>
                <SearchableDropdown
                  className="glr-owner-select"
                  options={[{ value: '', label: 'All accounts with activity' }, ...entityAccounts.slice().sort((a, b) => (a.account_number || '').localeCompare(b.account_number || '')).map(a => ({ value: String(a.coa_id), label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}` }))]}
                  value={ledgerAcct}
                  onChange={(v: string) => setLedgerAcct(v)}
                  placeholder="All accounts"
                />
              </div>
              {ledgerSections.length === 0 ? (
                <div className="glr-empty glr-empty-sm">No posted activity for this entity{(fromDate || toDate) ? ' in this date range' : ''}.</div>
              ) : ledgerSections.map(sec => {
                const isOpen = ledgerAcct ? true : openAccts.has(sec.acct.coa_id);
                return (
                  <div className="glr-acct" key={sec.acct.coa_id}>
                    <div className="glr-acct-head" onClick={() => !ledgerAcct && toggleAcct(sec.acct.coa_id)}>
                      {!ledgerAcct && (isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />)}
                      <span className="glr-acct-name">{sec.acct.account_number ? `${sec.acct.account_number} · ` : ''}{sec.acct.account_name}</span>
                      <span className="glr-acct-count">{sec.rows.length} line{sec.rows.length === 1 ? '' : 's'}</span>
                      <span className="glr-acct-ending">Balance {money(sec.ending)} {isCredit(sec.acct.normal_balance) ? 'Cr' : 'Dr'}</span>
                    </div>
                    {isOpen && (
                      <table className="glr-table glr-ledger-table">
                        <thead>
                          <tr><th>Date</th><th>Entry</th><th>Memo</th><th className="glr-num">Debit</th><th className="glr-num">Credit</th><th className="glr-num">Balance</th></tr>
                        </thead>
                        <tbody>
                          {includeOpening && (
                            <tr className="glr-opening">
                              <td colSpan={5}>Opening balance</td>
                              <td className="glr-num">{money(sec.opening)}</td>
                            </tr>
                          )}
                          {sec.rows.map(r => (
                            <tr key={r.line.line_id}>
                              <td>{(r.line._journal.journal_date || '').slice(0, 10)}</td>
                              <td>{r.line._journal.journal_number || `#${r.line._journal.journal_id}`}</td>
                              <td className="glr-memo">{r.line.line_memo || r.line._journal.memo || ''}</td>
                              <td className="glr-num">{r.debit > 0 ? money(r.debit) : ''}</td>
                              <td className="glr-num">{r.credit > 0 ? money(r.credit) : ''}</td>
                              <td className="glr-num">{money(r.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GLReportsTab;
